import { describeCondition, formatMetricValue, type AlertDto, type ClientDto } from '@citilight-monitor/contracts';
import { formatDateTime } from '../../lib/time';
import { EmptyState, StatusChip, StatusIndicator, Table } from '../atoms';

interface AlertsTableProps {
  alerts: AlertDto[];
  client: ClientDto;
}

/** Every alert and reminder sent for a client in the selected range, newest first. */
export function AlertsTable({ alerts, client }: AlertsTableProps) {
  const metrics = new Map(client.metrics.map((m) => [m.key, m]));

  if (!alerts.length) return <EmptyState>No alerts were sent in this range.</EmptyState>;

  return (
    <Table>
      <thead>
        <tr>
          <th scope="col">Sent</th>
          <th scope="col">Severity</th>
          <th scope="col">Condition</th>
          <th scope="col" className="num">
            Value
          </th>
          <th scope="col">Type</th>
          <th scope="col">Google Chat</th>
        </tr>
      </thead>
      <tbody>
        {alerts.map((alert) => {
          const metric = metrics.get(alert.metric);
          const unit = metric?.unit ?? 'number';
          return (
            <tr key={alert.id}>
              <td className="font-mono text-xs text-fg-secondary">{formatDateTime(alert.sentAt)}</td>
              <td>
                <StatusChip
                  tone={alert.severity === 'critical' ? 'critical' : 'warning'}
                  label={alert.severity === 'critical' ? 'Critical' : 'Warning'}
                />
              </td>
              <td>{describeCondition(metric?.label ?? alert.metric, unit, alert.operator, alert.threshold)}</td>
              <td className="num">{formatMetricValue(unit, alert.value)}</td>
              <td className="text-fg-secondary">
                {alert.kind === 'fired' ? 'Fired' : 'Reminder'}
                <span className="text-fg-muted"> · after {alert.consecutiveBreaches} polls</span>
              </td>
              <td title={alert.error}>
                {alert.delivered ? (
                  <StatusIndicator tone="good" label="Delivered" />
                ) : (
                  <StatusIndicator tone="critical" label={alert.error ? `Failed: ${alert.error}` : 'Failed'} />
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
